import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, ChevronLeft, ChevronRight, CalendarClock, CheckCircle2, Clock, Trash2, RotateCcw, PauseCircle } from "lucide-react";
import { formatDayLabel, currentCycleDates } from "@/lib/schedule";
import { attendanceChipClass, attendanceDotClass } from "@/lib/attendanceColors";
import { todayISO } from "@/lib/dateFormat";

/**
 * Admin viewer: shows a single student's attendance for a single group,
 * cycle by cycle (same visual as the student's "Mi ciclo actual").
 *
 * Props:
 *   open, onOpenChange
 *   student  {student_id, name}
 *   group    {id, name, schedule_days, created_at, program_id, program_name}
 *   enrollment {id, start_date, classes_included}
 *   totalPerCycle number (derived from program.classes_included)
 */
export default function StudentAttendanceDialog({ open, onOpenChange, student, group, enrollment, totalPerCycle, onChanged }) {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  // Editar asistencia desde acá es exclusivo del super admin (decisión 10/09/2026).
  const canEdit = user?.role === "superadmin";
  const canRemove = canEdit;
  const [editTarget, setEditTarget] = useState(null); // {date, status}
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [reschedules, setReschedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cycleOffset, setCycleOffset] = useState(0);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!open || !student?.student_id || !group?.id) return;
    setLoading(true);
    setCycleOffset(0);
    // Fetch attendance AND extra sessions in parallel so reschedules are
    // reflected in the cycle strip (mirrors the teacher's attendance view).
    Promise.all([
      api.get("/attendance", { params: { group_id: group.id, student_id: student.student_id } }),
      api.get("/extra-sessions", { params: { group_id: group.id } }),
      enrollment?.id
        ? api.get(`/enrollments/${enrollment.id}/consumption/detail`).catch(() => ({ data: null }))
        : Promise.resolve({ data: null }),
    ])
      .then(([rAtt, rEx, rDet]) => { setRows(rAtt.data || []); setReschedules(rEx.data || []); setDetail(rDet.data); })
      .catch(() => { setRows([]); setReschedules([]); setDetail(null); })
      .finally(() => setLoading(false));
  }, [open, student?.student_id, group?.id, enrollment?.id]);

  const today = todayISO();
  const startISO = enrollment?.start_date || (group?.created_at ? group.created_at.slice(0, 10) : null);

  const classified = useMemo(() => {
    const cc = currentCycleDates({
      group, enrollment, extraSessions: reschedules, attendance: rows,
      totalPerCycle: totalPerCycle || 8, today,
    });
    return cc ? cc.classified : [];
  }, [rows, reschedules, group, enrollment, totalPerCycle, today]);

  // Which cycle contains today
  const { currentCycle, targetCycle, cycleDates, consumedInCycle } = useMemo(() => {
    let cur = 1;
    for (const d of classified) {
      if (d.date <= today) cur = d.cycle;
      else break;
    }
    const target = cur + cycleOffset;
    const dates = classified.filter((d) => d.cycle === target);
    let consumed = dates.filter((d) => d.consumes && d.status).length;
    // For the very first cycle, add the pre-existing consumption declared by
    // the admin via `starts_on_day` (sessions the student had already taken
    // before joining the system's tracking).
    if (target === 1) {
      consumed += Number(enrollment?.starts_on_day || 0);
    }
    return { currentCycle: cur, targetCycle: target, cycleDates: dates, consumedInCycle: consumed };
  }, [classified, cycleOffset, enrollment?.starts_on_day]);

  const refresh = async () => {
    const [rAtt, rDet] = await Promise.all([
      api.get("/attendance", { params: { group_id: group.id, student_id: student.student_id } }),
      enrollment?.id
        ? api.get(`/enrollments/${enrollment.id}/consumption/detail`).catch(() => ({ data: null }))
        : Promise.resolve({ data: null }),
    ]);
    setRows(rAtt.data || []);
    setDetail(rDet.data);
  };

  // Cambia el estado de una fecha (o le quita la marca con next=null) y deja que
  // el backend recalcule el contador del ciclo.
  const applyStatus = async (next) => {
    if (!editTarget) return;
    const d = editTarget.date;
    setSaving(true);
    try {
      if (next === null) {
        await api.delete("/attendance", {
          params: { student_id: student.student_id, group_id: group.id, date: d },
        });
      } else {
        await api.post("/attendance", {
          student_id: student.student_id, group_id: group.id, date: d,
          status: next, notes: reason,
        });
      }
      await refresh();
      toast.success(next === null ? t("removed_reverted") : t("att_edit_done"));
      onChanged?.();
      setEditTarget(null);
      setReason("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <CalendarClock className="h-4 w-4 text-brand-blue" />
            Asistencia · {student?.name} · {group?.name}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="p-10 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
              <div className="text-xs text-slate-500">
                {group?.program_name || ""} · Inicio: <span className="font-mono">{startISO || "—"}</span>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" disabled={targetCycle <= 1} onClick={() => setCycleOffset((v) => v - 1)} data-testid="admin-att-prev-cycle">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-3 text-xs font-semibold text-slate-700 min-w-[110px] text-center" data-testid="admin-att-cycle-label">
                  {cycleOffset === 0 ? t("cycle_current") : `${t("cycle")} ${targetCycle}`}
                </span>
                <Button variant="outline" size="icon" onClick={() => setCycleOffset((v) => v + 1)} data-testid="admin-att-next-cycle">
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setCycleOffset(0)} data-testid="admin-att-this-cycle">
                  {t("cycle_current")}
                </Button>
              </div>
            </div>

            <div className="mb-2 text-xs font-semibold text-slate-700">
              {t("cycle")} {targetCycle} · {consumedInCycle}/{totalPerCycle || 8}
            </div>

            {/* Este aviso (y el borrado de asistencias) es solo para el super
                admin: las profes no tienen que ver ni tocar esta información. */}
            {canEdit && detail && cycleOffset === 0 && detail.consumed_in_cycle !== consumedInCycle && (
              <div className="mb-3 text-xs bg-amber-50 border border-amber-200 rounded-md px-3 py-2 space-y-2" data-testid="att-consumption-mismatch">
                <div className="text-amber-800">
                  {t("cycle_mismatch_warning")}
                  <span className="font-semibold"> {detail.consumed_in_cycle}/{detail.classes_included}</span>.
                </div>
                {(() => {
                  const inStrip = new Set(cycleDates.map((d) => d.date));
                  const extras = (detail.records || []).filter((r) => r.counts && !inStrip.has(r.date));
                  if (!extras.length) return null;
                  return (
                    <div className="space-y-1.5">
                      <div className="text-amber-800">{t("cycle_mismatch_dates")}:</div>
                      {extras.map((r) => (
                        <div key={r.date} className="flex items-center justify-between gap-2 bg-white/70 rounded px-2 py-1">
                          <span className="text-slate-700">
                            {r.date} · {["present", "absent", "justified", "unjustified"].includes(r.status) ? t(r.status) : r.status}
                            {r.extra_session
                              ? ` · ${t("cycle_mismatch_extra")}`
                              : ` · ${t("cycle_mismatch_offschedule")}`}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-red-600 hover:text-red-700"
                            data-testid={`delete-offcycle-${r.date}`}
                            onClick={async () => {
                              if (!window.confirm(`${t("cycle_mismatch_delete_confirm")} ${r.date}?`)) return;
                              try {
                                await api.delete("/attendance", {
                                  params: { student_id: student.student_id, group_id: group.id, date: r.date },
                                });
                                const [rAtt, rDet] = await Promise.all([
                                  api.get("/attendance", { params: { group_id: group.id, student_id: student.student_id } }),
                                  api.get(`/enrollments/${enrollment.id}/consumption/detail`),
                                ]);
                                setRows(rAtt.data || []);
                                setDetail(rDet.data);
                                onChanged?.();
                              } catch (e) { /* keep dialog open */ }
                            }}
                          >
                            {t("delete")}
                          </Button>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}

            {cycleDates.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-400 italic border border-dashed border-slate-200 rounded-md">
                {t("no_dates_in_cycle")}
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {cycleDates.map(({ date: d, status, paused }) => {
                  const isToday = d === today;
                  let cls = attendanceChipClass(paused ? "paused" : status);
                  if (!status && !paused && isToday) cls = "bg-blue-100 text-blue-900 border-blue-500";
                  const base = `px-2.5 py-1 rounded-md text-[11px] font-semibold border flex items-center gap-1.5 ${cls}`;
                  const label = (
                    <>
                      {paused && <PauseCircle className="h-3 w-3" />}
                      {!paused && status === "present" && <CheckCircle2 className="h-3 w-3" />}
                      {!paused && status === "removed" && <Trash2 className="h-3 w-3" />}
                      {!paused && !status && <Clock className="h-3 w-3" />}
                      {formatDayLabel(d, lang)}
                    </>
                  );
                  if (!canEdit || paused) {
                    return (
                      <div key={d} data-testid={`admin-att-chip-${d}`} className={base}
                           title={paused ? `${d} — ${t("att_paused_hint")}` : status ? `${d} — ${t(status)}` : d}>
                        {label}
                      </div>
                    );
                  }
                  return (
                    <button
                      type="button"
                      key={d}
                      data-testid={`admin-att-chip-${d}`}
                      onClick={() => { setReason(""); setEditTarget({ date: d, status }); }}
                      className={`${base} hover:ring-2 hover:ring-offset-1 hover:ring-slate-400 transition-shadow`}
                      title={t("att_edit_chip_hint")}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 mt-4 text-[10px] text-slate-500">
              {["present","justified","unjustified","teacher_cancelled",...(canRemove ? ["removed"] : []),""].map((s) => (
                <span key={s || "pending"} className="flex items-center gap-1">
                  <span className={`inline-block h-2.5 w-2.5 rounded-full ${attendanceDotClass(s)}`} />
                  {s ? t(s) : t("upcoming")}
                </span>
              ))}
            </div>
          </>
        )}

        {/* Editar el estado de una fecha (solo super admin) */}
        <Dialog open={!!editTarget} onOpenChange={(o) => { if (!o) { setEditTarget(null); setReason(""); } }}>
          <DialogContent className="max-w-md" data-testid="edit-attendance-dialog" aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle className="text-base">
                {editTarget ? formatDayLabel(editTarget.date, lang) : ""}
              </DialogTitle>
            </DialogHeader>
            <p className="text-xs text-slate-500">
              {student?.name} · {group?.name}
              {editTarget?.status
                ? <> · {t("att_edit_now_in")} <span className="font-semibold text-slate-700">{t(editTarget.status)}</span></>
                : <> · {t("att_edit_unmarked")}</>}
            </p>
            <div className="grid gap-1.5">
              {["present", "justified", "unjustified", "teacher_cancelled", "removed"].map((s) => (
                <Button
                  key={s}
                  variant={editTarget?.status === s ? "default" : "outline"}
                  className="justify-start h-9"
                  disabled={saving || editTarget?.status === s}
                  data-testid={`edit-att-status-${s}`}
                  onClick={() => applyStatus(s)}
                >
                  <span className={`inline-block h-2.5 w-2.5 rounded-full mr-2 ${attendanceDotClass(s)}`} />
                  {t(s)}
                  {s === "removed" && <span className="ml-2 text-[10px] text-slate-400">{t("att_edit_no_consume")}</span>}
                </Button>
              ))}
              {editTarget?.status && (
                <Button variant="ghost" className="justify-start h-9 text-slate-600" disabled={saving}
                        data-testid="edit-att-status-clear" onClick={() => applyStatus(null)}>
                  <RotateCcw className="h-3.5 w-3.5 mr-2" /> {t("att_edit_clear")}
                </Button>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">{t("att_edit_note_label")}</label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)}
                     placeholder={t("att_edit_note_placeholder")}
                     data-testid="edit-att-note" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditTarget(null)} disabled={saving}>{t("cancel")}</Button>
              {saving && <Loader2 className="h-4 w-4 animate-spin text-slate-400 self-center" />}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
